/**
 * InfoPanel — two compact chips sliding out to the right of the `i` button.
 *
 * Figma source: node `40000026:11865` `tags` frame — 131×22, gap 4.
 *   - chip 1: resolution (e.g. «2K»)      60×22
 *   - chip 2: aspect ratio (e.g. «9:16»)  67×22
 *   - both: glass-white@12% + backdrop-blur 32, cornerRadius 16,
 *           padding 14/0/14/0, Raleway SemiBold 14, white text.
 *
 * Photo Generation stores the requested quality in the preset token
 * (`GENERAL_1.5K`, `GENERAL_2K`) and may also store width/height. Prefer
 * the explicit quality, then fall back to pixel dimensions for legacy rows.
 */
import { ViewerInfoChip, ViewerInfoPanel } from '../ViewerInfoChips';

function resolutionLabel(width, height, quality, hasPhotoMetadata) {
  const qualityValue = typeof quality === 'string' ? quality.toUpperCase() : '';
  if (qualityValue.includes('4K')) return '4K';
  if (/1(?:\.5)?K/.test(qualityValue)) return '1K';
  if (qualityValue.includes('2K')) return '2K';
  if (qualityValue === 'HQ') return '2K';
  if (qualityValue.includes('HD')) return 'HD';

  const longest = Math.max(Number(width) || 0, Number(height) || 0);
  if (longest >= 3840) return '4K';
  if (longest >= 2048) return '2K';
  if (longest >= 1280) return 'HD';
  if (longest > 0) return `${longest}p`;
  if (hasPhotoMetadata) return '2K';
  return null;
}

function aspectRatioLabel(aspectRatio, width, height) {
  if (aspectRatio) return aspectRatio;

  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) return null;

  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const divisor = gcd(w, h);
  return `${Math.round(w / divisor)}:${Math.round(h / divisor)}`;
}

export default function InfoPanel({ item, onClose }) {
  const { aspectRatio, width, height, quality, imageUrl } = item || {};
  const resolution = resolutionLabel(width, height, quality, Boolean(imageUrl || aspectRatio));
  const ratio = aspectRatioLabel(aspectRatio, width, height);

  return (
    <ViewerInfoPanel onClose={onClose}>
      {resolution && (
        <ViewerInfoChip data-testid="chip-quality">{resolution}</ViewerInfoChip>
      )}
      {ratio && <ViewerInfoChip data-testid="chip-ratio">{ratio}</ViewerInfoChip>}
    </ViewerInfoPanel>
  );
}
